// Atmospheric clouds: one instanced mesh of drifting puffs at high altitude over the board, plus the
// uniforms that drive the soft cloud shadows on the terrain (see cloudShadow.ts). Seeded layout,
// climate-aware cover, a static sky under prefers-reduced-motion, and a perf guard that switches the
// whole thing off on slow GPUs. Puffs fade out near the camera and when the camera rises above the layer,
// so they never hide a unit or a tile.

import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { cloudGeo } from './Ambient.js';
import {
  CLOUD_LAYER_MAX_Y,
  type CloudBounds,
  CloudPerfGuard,
  cloudCount,
  cloudCover,
  cloudLayout,
  cloudPosition,
  cloudShadowStrength,
} from './atmosphereClouds.js';
import { cloudShadowUniforms } from './cloudShadow.js';
import { renderStats } from './renderStats.js';

const _obj = new THREE.Object3D();

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

const material = (() => {
  const m = new THREE.MeshBasicMaterial({
    color: '#f2f6fa',
    transparent: true,
    opacity: 0.5,
    fog: false,
    depthWrite: false,
  });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uLayerTop = { value: CLOUD_LAYER_MAX_Y };
    shader.vertexShader =
      `uniform float uLayerTop;\nvarying float _vCloudFade;\n${shader.vertexShader}`.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
      {
        vec4 _wp = modelMatrix * instanceMatrix * vec4(transformed, 1.0);
        float _d = distance(cameraPosition, _wp.xyz);
        // Fade near the camera, and fade the whole layer out once the camera climbs above it.
        _vCloudFade = smoothstep(5.0, 12.0, _d) * (1.0 - smoothstep(0.0, 4.0, cameraPosition.y - uLayerTop));
      }`,
      );
    shader.fragmentShader = `varying float _vCloudFade;\n${shader.fragmentShader}`.replace(
      '#include <color_fragment>',
      `#include <color_fragment>
      diffuseColor.a *= _vCloudFade;`,
    );
  };
  m.customProgramCacheKey = () => 'atmos-clouds';
  return m;
})();

export function AtmosphericClouds({
  bounds,
  climate,
  enabled,
}: {
  readonly bounds: CloudBounds;
  readonly climate?: string;
  readonly enabled: boolean;
}) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const reduced = useMemo(prefersReducedMotion, []);
  const guard = useMemo(() => new CloudPerfGuard(), []);
  const count = cloudCount(climate);
  const puffs = useMemo(() => cloudLayout(bounds, count), [bounds, count]);
  const t = useRef(0);

  // Terrain shadows: strength and cover follow the climate; 0 when off.
  useEffect(() => {
    cloudShadowUniforms.uCloudCover.value = cloudCover(climate);
    cloudShadowUniforms.uCloudStrength.value = enabled ? cloudShadowStrength(climate) : 0;
    return () => {
      cloudShadowUniforms.uCloudStrength.value = 0;
    };
  }, [climate, enabled]);

  useFrame((_, delta) => {
    if (!enabled) return;
    guard.sample(renderStats.fps, delta);
    if (guard.degraded) {
      cloudShadowUniforms.uCloudStrength.value = 0;
      if (ref.current) ref.current.visible = false;
      return;
    }
    if (!reduced) t.current += delta;
    cloudShadowUniforms.uCloudTime.value = t.current;
    const mesh = ref.current;
    if (!mesh) return;
    for (let i = 0; i < puffs.length; i++) {
      const p = puffs[i]!;
      const [x, y, z] = cloudPosition(p, t.current, bounds, reduced);
      _obj.position.set(x, y, z);
      _obj.rotation.set(0, 0, 0);
      _obj.scale.set(p.scale * 1.5, p.scale * 0.32, p.scale);
      _obj.updateMatrix();
      mesh.setMatrixAt(i, _obj.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });

  if (!enabled || guard.degraded) return null;
  return (
    <instancedMesh
      ref={ref}
      args={[cloudGeo, material, puffs.length]}
      frustumCulled={false}
      raycast={() => null}
      renderOrder={5}
    />
  );
}
